import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { IMaskInput } from 'react-imask'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { apiFetch } from '../../lib/api'
import { brl } from '../../lib/format'
import { useAuth } from '../../state/auth'

type SimulationResult = {
  salaryBase: number
  salaryEffective?: number
  trustBonus?: number
  inssEmployee: number
  irrf?: number
  netSalaryEstimate?: number
  inssEmployer: number
  fgts: number
  unhealthyAllowance: number
  periculosidadeAllowance?: number
  provision13th: number
  provisionVacationPlusThird: number
  totalMonthly: number
  totalAnnual: number
}

type CalcResponse = {
  result: SimulationResult
  applied: {
    regime: 'simples' | 'presumido' | 'real'
    anexo?: 'I' | 'II' | 'III' | 'IV'
    fgtsRate: number
    inssEmployerRate: number
    minWageBase: number
    periculosidade: boolean
  }
}

const ANEXOS_OPTIONS = [
  { value: 'I' as const, label: 'Anexo I – Comércio' },
  { value: 'II' as const, label: 'Anexo II – Indústria' },
  { value: 'III' as const, label: 'Anexo III – Serviços (sem INSS Patronal)' },
  { value: 'IV' as const, label: 'Anexo IV – Serviços (com INSS Patronal de 20%)' }
]

const ANEXOS_SIMPLES_LABEL: Record<string, string> = {
  I: 'Anexo I - Comércio',
  II: 'Anexo II - Indústria',
  III: 'Anexo III - Serviços (sem INSS Patronal)',
  IV: 'Anexo IV - Serviços (com INSS Patronal de 20%)'
}

const REGIME_LABELS: Record<string, string> = {
  simples: 'Simples Nacional',
  presumido: 'Presumido',
  real: 'Real'
}

const schema = z
  .object({
    baseSalary: z.string().min(1, 'Informe o salário'),
    trustPosition: z.boolean().default(false),
    unhealthyPercent: z.coerce
      .number()
      .refine((v) => [0, 20, 40].includes(v), 'Selecione 0%, 20% ou 40%')
      .default(0),
    periculosidade: z.boolean().default(false),
    dependentsCount: z.coerce.number().int().min(0).max(20).default(0),
    regime: z.enum(['simples', 'presumido', 'real']).default('presumido'),
    anexo: z.enum(['I', 'II', 'III', 'IV']).default('I')
  })

type FormData = z.input<typeof schema>

export function CostSimulationPage() {
  const { selectedCompanyId } = useAuth()
  const qc = useQueryClient()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { trustPosition: false, unhealthyPercent: 0, periculosidade: false, dependentsCount: 0, regime: 'presumido', anexo: 'I' }
  })

  const trustPosition = watch('trustPosition') ?? false
  const unhealthyPercent = watch('unhealthyPercent') ?? 0
  const regime = watch('regime') ?? 'presumido'
  const periculosidade = watch('periculosidade') ?? false

  const settingsQ = useQuery({
    queryKey: ['company-settings', selectedCompanyId],
    enabled: !!selectedCompanyId,
    queryFn: () => apiFetch<{ minWageBase: number }>(`/companies/${selectedCompanyId}/settings`),
    staleTime: 0
  })
  const minWageBase = settingsQ.data?.minWageBase ?? 1518

  type PdfOptions = {
    regime: 'simples' | 'presumido' | 'real'
    anexo?: 'I' | 'II' | 'III' | 'IV'
    dependentsCount: number
  }

  const exportPdf = async (result: SimulationResult, options: PdfOptions) => {
    const doc = new jsPDF()
    const margin = 14
    const pageWidth = doc.internal.pageSize.getWidth()
    let y = 16

    // Adicionar logo no topo (centralizada)
    try {
      const logoImg = await fetch('/logo-pdf.png').then(r => r.blob()).then(b => new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.readAsDataURL(b)
      }))
      const logoWidth = 60
      const logoHeight = 28
      const logoX = (pageWidth - logoWidth) / 2
      doc.addImage(logoImg, 'PNG', logoX, y, logoWidth, logoHeight)
      y += logoHeight + 10
    } catch (e) {
      console.warn('Não foi possível carregar a logo', e)
    }

    const remunerationBase =
      (typeof result.salaryEffective === 'number' ? result.salaryEffective : result.salaryBase) +
      (result.unhealthyAllowance ?? 0) +
      (result.periculosidadeAllowance ?? 0)
    const provisionAvisoPrevio = Math.round((remunerationBase / 12) * 100) / 100
    const totalMensalComAviso = result.totalMonthly + provisionAvisoPrevio
    const totalAnualComAviso = result.totalAnnual + provisionAvisoPrevio * 12

    const drawBox = (yStart: number, yEnd: number, title?: string) => {
      doc.setDrawColor(180, 180, 180)
      doc.setLineWidth(0.3)
      doc.rect(margin, yStart - (title ? 5 : 0), pageWidth - margin * 2, yEnd - yStart + (title ? 5 : 0))
    }

    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('Relatório de Custo Total do Funcionário', margin, y)
    y += 8
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, y)
    y += 12

    // ——— BLOCOS COM SEPARAÇÃO VISUAL ———

    // 1. DADOS DA SIMULAÇÃO (caixa)
    const yDadosStart = y
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text('DADOS DA SIMULAÇÃO', margin + 2, y + 5)
    y += 10
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.text(`Regime Tributário: ${REGIME_LABELS[options.regime] ?? options.regime}`, margin + 4, y)
    y += 6
    if (options.regime === 'simples' && options.anexo) {
      doc.text(`Anexo: ${ANEXOS_SIMPLES_LABEL[options.anexo] ?? options.anexo}`, margin + 4, y)
      y += 6
    }
    doc.text(`Número de Dependentes: ${options.dependentsCount}`, margin + 4, y)
    y += 10
    drawBox(yDadosStart, y, 'DADOS DA SIMULAÇÃO')
    y += 6

    // 2. RESUMO FINANCEIRO (caixa + tabela)
    const yResumoStart = y
    doc.setFont('helvetica', 'bold')
    doc.text('RESUMO FINANCEIRO', margin + 2, y + 5)
    y += 10
    autoTable(doc, {
      startY: y,
      head: [['Descrição', 'Valor Mensal', 'Valor Anual']],
      body: [
        ['Salário Base', brl.format(remunerationBase), brl.format(remunerationBase * 12)],
        ['(-) INSS', brl.format(result.inssEmployee), brl.format(result.inssEmployee * 12)],
        ['(-) IRRF', brl.format(result.irrf ?? 0), brl.format((result.irrf ?? 0) * 12)],
        ['= Salário Líquido', brl.format(result.netSalaryEstimate ?? 0), brl.format((result.netSalaryEstimate ?? 0) * 12)]
      ],
      margin: { left: margin + 2, right: margin },
      theme: 'grid',
      headStyles: { fontStyle: 'bold', fillColor: [97, 110, 179], textColor: 255 },
      tableWidth: 'auto'
    })
    y = (doc as any).lastAutoTable.finalY + 6
    drawBox(yResumoStart, y, 'RESUMO FINANCEIRO')
    y += 6

    // 3. DETALHAMENTO DOS ENCARGOS PATRONAIS (caixa + tabela)
    const yEncargosStart = y
    doc.setFont('helvetica', 'bold')
    doc.text('DETALHAMENTO DOS ENCARGOS PATRONAIS', margin + 2, y + 5)
    y += 10
    autoTable(doc, {
      startY: y,
      head: [['Encargo/Provisão', 'Valor Mensal', 'Valor Anual']],
      body: [
        ['FGTS (8%)', brl.format(result.fgts), brl.format(result.fgts * 12)],
        ['Provisão Férias + 1/3', brl.format(result.provisionVacationPlusThird), brl.format(result.provisionVacationPlusThird * 12)],
        ['Provisão 13º Salário', brl.format(result.provision13th), brl.format(result.provision13th * 12)],
        ['Provisão Aviso Prévio', brl.format(provisionAvisoPrevio), brl.format(provisionAvisoPrevio * 12)]
      ],
      margin: { left: margin + 2, right: margin },
      theme: 'grid',
      headStyles: { fontStyle: 'bold', fillColor: [97, 110, 179], textColor: 255 },
      tableWidth: 'auto'
    })
    y = (doc as any).lastAutoTable.finalY + 6
    drawBox(yEncargosStart, y, 'DETALHAMENTO')
    y += 6

    // 4. CUSTO TOTAL PARA A EMPRESA (caixa destacada)
    const yCustoStart = y
    doc.setDrawColor(100, 100, 100)
    doc.setLineWidth(0.5)
    doc.setFillColor(240, 240, 240)
    doc.rect(margin, yCustoStart, pageWidth - margin * 2, 22, 'FD')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.text('CUSTO TOTAL PARA A EMPRESA', margin + 4, yCustoStart + 8)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.text(`Mensal: ${brl.format(totalMensalComAviso)}  |  Anual: ${brl.format(totalAnualComAviso)}`, margin + 4, yCustoStart + 16)
    y = yCustoStart + 28

    doc.setFontSize(9)
    doc.setTextColor(100, 100, 100)
    doc.text('Este documento é uma simulação para fins de planejamento.', margin, y)
    doc.setTextColor(0, 0, 0)

    doc.save(`custo_funcionario_${result.salaryBase.toFixed(2).replace('.', '_')}_${new Date().toISOString().slice(0, 10)}.pdf`)
  }

  const onSubmit = async (data: FormData) => {
    const payload: Record<string, unknown> = {
      baseSalary: data.baseSalary,
      trustPosition: data.trustPosition,
      unhealthyPercent: data.unhealthyPercent,
      periculosidade: data.periculosidade,
      dependentsCount: data.dependentsCount,
      regime: data.regime,
      minWageBase
    }
    if (selectedCompanyId) payload.companyId = selectedCompanyId
    if (data.regime === 'simples') payload.anexo = data.anexo ?? 'I'
    const resp = await apiFetch<CalcResponse>(`/calc/employee-cost`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
    toast.success('Cálculo gerado')
    await qc.invalidateQueries()
    const regimeValue = (data.regime ?? 'presumido') as 'simples' | 'presumido' | 'real'
    await exportPdf(resp.result, {
      regime: regimeValue,
      anexo: regimeValue === 'simples' ? (data.anexo ?? 'I') : undefined,
      dependentsCount: data.dependentsCount ?? 0
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xl font-semibold text-slate-900">Cálculo de Custo do Funcionário</div>
        <div className="mt-1 text-sm text-slate-500">
          Simulador manual para estimar custo mensal/anual (com PDF), sem necessidade de cadastrar empresa ou funcionário.
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-sm font-semibold text-slate-900">Simulação</div>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700">Salário base (R$)</label>
              <IMaskInput
                mask={Number}
                radix=","
                thousandsSeparator="."
                scale={2}
                padFractionalZeros
                normalizeZeros
                className="mt-1 h-11 w-full max-w-xs rounded-xl border border-slate-200 px-4 text-sm"
                onAccept={(v) => setValue('baseSalary', String(v))}
              />
              {errors.baseSalary && <div className="mt-1 text-xs text-red-600">{errors.baseSalary.message}</div>}
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700">Regime tributário</label>
              <select className="mt-3 h-11 w-full md:max-w-xs rounded-xl border border-slate-200 px-4 text-sm" {...register('regime')}>
                <option value="simples">Simples Nacional</option>
                <option value="presumido">Presumido</option>
                <option value="real">Real</option>
              </select>
              {regime === 'simples' ? (
                <div className="mt-4 rounded-xl border-2 border-indigo-200 bg-indigo-50/50 p-4">
                  <label className="text-sm font-semibold text-slate-800">Anexo do Simples Nacional</label>
                  <p className="mt-1 text-xs text-slate-600">Selecione o anexo conforme o setor da empresa. Apenas o Anexo IV inclui INSS Patronal (20%).</p>
                  <select className="mt-2 h-11 w-full max-w-md rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-900" {...register('anexo')}>
                    {ANEXOS_OPTIONS.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-slate-500">
                    Anexo IV: INSS Patronal <span className="font-semibold text-indigo-700">20%</span> no valor a pagar. Anexos I, II e III: sem INSS Patronal.
                  </p>
                </div>
              ) : (
                <div className="mt-1 text-xs text-slate-500">
                  FGTS 8% e INSS patronal <span className="font-medium">20%</span>.
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" className="h-4 w-4" {...register('trustPosition')} />
                Cargo de confiança
              </label>
              {trustPosition ? (
                <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-xs text-indigo-900">
                  <div className="font-semibold">Atenção</div>
                  <div className="mt-1">
                    Funcionários com cargo de confiança recebem adicional de <span className="font-semibold">40%</span> sobre o salário base, conforme legislação trabalhista.
                  </div>
                </div>
              ) : null}

              <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" className="h-4 w-4" {...register('periculosidade')} />
                Periculosidade (30% sobre o salário base)
              </label>
              <div className="text-xs text-slate-500">
                Se marcado, adiciona <span className="font-medium">30%</span> sobre o salário base informado na simulação.
                {periculosidade ? <span className="ml-1 font-medium text-slate-700">(Selecionado)</span> : null}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Insalubridade</label>
              <select className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm" {...register('unhealthyPercent')}>
                <option value={0}>Sem insalubridade</option>
                <option value={20}>20% (grau médio)</option>
                <option value={40}>40% (grau máximo)</option>
              </select>
              <div className="mt-1 text-xs text-slate-500">
                A insalubridade é calculada sobre a base configurada (atual: <span className="font-medium">{brl.format(minWageBase)}</span>) e integra a base de cálculo dos encargos.
                {!selectedCompanyId && (
                  <span className="ml-1 text-slate-400">Selecione uma empresa no topo para usar a base definida em Configurações.</span>
                )}
                {unhealthyPercent > 0 ? <span className="ml-1 font-medium text-slate-700">(Selecionado: {unhealthyPercent}%)</span> : null}
              </div>
            </div>

            <div className="md:col-start-1 md:row-start-2">
              <label className="text-sm font-medium text-slate-700">Número de dependentes (IRRF)</label>
              <input type="number" min={0} max={20} className="mt-1 h-11 w-full max-w-xs rounded-xl border border-slate-200 px-4 text-sm" {...register('dependentsCount')} />
            </div>
          </div>

          <button
            disabled={isSubmitting}
            className="h-11 w-full rounded-xl bg-slate-900 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            type="submit"
          >
            {isSubmitting ? 'Calculando…' : 'Calcular e exportar PDF'}
          </button>
        </form>
      </div>
    </div>
  )
}

